import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Drawer from "components/drawerSlide/drawer";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { Select } from "components/select/index";
import { ButtonNew as Button } from "components/buttonNew";
import "./accountsFilterDrawer.scss";

export type AccountBatchJobsDrawerFilters = {
  filterDate: Date;
  useDateFilter: boolean;
  jobStatusFilter: string;
  statusFilter: string;
};

export function emptyAccountBatchJobsDrawerFilters(): AccountBatchJobsDrawerFilters {
  return {
    filterDate: new Date(),
    useDateFilter: false,
    jobStatusFilter: "",
    statusFilter: "",
  };
}

export function countActiveAccountBatchJobsFilters(filters: AccountBatchJobsDrawerFilters): number {
  let count = 0;
  if (filters.useDateFilter) {
    count += 1;
  }
  if (filters.jobStatusFilter) {
    count += 1;
  }
  if (filters.statusFilter) {
    count += 1;
  }
  return count;
}

type Props = {
  open: boolean;
  appliedFilters: AccountBatchJobsDrawerFilters;
  onClose: () => void;
  onApply: (filters: AccountBatchJobsDrawerFilters) => void;
};

export default function AccountBatchJobsFilterDrawer(props: Props) {
  const { open, appliedFilters, onClose, onApply } = props;
  const { t } = useTranslation();
  const [draft, setDraft] = useState<AccountBatchJobsDrawerFilters>(emptyAccountBatchJobsDrawerFilters());
  const [draftKey, setDraftKey] = useState(0);

  useEffect(() => {
    if (open) {
      setDraft({ ...appliedFilters });
      setDraftKey(prev => prev + 1);
    }
  }, [open, appliedFilters]);

  const activeFilterCount = countActiveAccountBatchJobsFilters(draft);

  function handleClearAll() {
    setDraft(emptyAccountBatchJobsDrawerFilters());
    setDraftKey(prev => prev + 1);
  }

  function handleApply() {
    onApply(draft);
    onClose();
  }

  return (
    <div className="accounts-filter-drawer">
      <Drawer
        open={open}
        title={t("Filters")}
        subtitle={t("Apply filters to the batches table.")}
        onClose={onClose}
        onOk={handleApply}
        okText={
          activeFilterCount > 0
            ? t("Apply {{count}} filters", { count: activeFilterCount })
            : t("Apply filters")
        }
        showCancel
        cancelText={t("Cancel")}
        cancelButtonType="text"
        secondaryActions={[{ label: t("Clear all"), action: handleClearAll, buttonType: "secondary" }]}
        backDropClose
      >
        <Drawer.Section bordered>
          <DatePickerInput
            key={`date-${draftKey}-${draft.useDateFilter ? moment(draft.filterDate).format("YYYY-MM-DD") : "all"}`}
            label={t("Created Date")}
            months={1}
            position="left"
            startingDate={draft.filterDate}
            setStartingDate={date => {
              const next = typeof date === "function" ? date(draft.filterDate) : date;
              if (!next) {
                return;
              }
              setDraft(prev => ({ ...prev, filterDate: next, useDateFilter: true }));
            }}
          />
          {draft.useDateFilter ? (
            <Button
              type="text"
              size="small"
              onClick={() => setDraft(prev => ({ ...prev, useDateFilter: false }))}
            >
              {t("Clear date")}
            </Button>
          ) : (
            <p className="table-cell-support-text">{t("Showing all dates")}</p>
          )}
        </Drawer.Section>

        <Drawer.Section bordered>
          <Select
            key={`job-status-${draftKey}-${draft.jobStatusFilter || "any"}`}
            label={t("Batch Status")}
            defaultValue={draft.jobStatusFilter}
            onChange={(value: string | number) => {
              setDraft(prev => ({
                ...prev,
                jobStatusFilter: value == null ? "" : String(value),
              }));
            }}
          >
            <Select.Option value="" name={t("Any status")}>
              <span>{t("Any status")}</span>
            </Select.Option>
            <Select.Option value="draft" name={t("Draft")}>
              <span>{t("Draft")}</span>
            </Select.Option>
            <Select.Option value="scheduled" name={t("Scheduled")}>
              <span>{t("Scheduled")}</span>
            </Select.Option>
            <Select.Option value="pending" name={t("Pending")}>
              <span>{t("Pending")}</span>
            </Select.Option>
            <Select.Option value="processing" name={t("Processing")}>
              <span>{t("Processing")}</span>
            </Select.Option>
            <Select.Option value="complete" name={t("Complete")}>
              <span>{t("Complete")}</span>
            </Select.Option>
            <Select.Option value="partial" name={t("Partial")}>
              <span>{t("Partial")}</span>
            </Select.Option>
            <Select.Option value="failed" name={t("Failed")}>
              <span>{t("Failed")}</span>
            </Select.Option>
          </Select>
        </Drawer.Section>

        <Drawer.Section>
          <Select
            key={`status-${draftKey}-${draft.statusFilter || "any"}`}
            label={t("Account Result")}
            defaultValue={draft.statusFilter}
            onChange={(value: string | number) => {
              setDraft(prev => ({
                ...prev,
                statusFilter: value == null ? "" : String(value),
              }));
            }}
          >
            <Select.Option value="" name={t("Any status")}>
              <span>{t("Any status")}</span>
            </Select.Option>
            <Select.Option value="success" name={t("Succeeded")}>
              <span>{t("Succeeded")}</span>
            </Select.Option>
            <Select.Option value="failed" name={t("Failed")}>
              <span>{t("Failed")}</span>
            </Select.Option>
            <Select.Option value="pending" name={t("Pending")}>
              <span>{t("Pending")}</span>
            </Select.Option>
          </Select>
        </Drawer.Section>
      </Drawer>
    </div>
  );
}
